var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import cl from "clsx";
import React, { forwardRef, useEffect, useRef, useState, } from "react";
import { CheckmarkIcon, FilesIcon } from "@navikt/aksel-icons";
import { Label } from "../typography/index.js";
import { composeEventHandlers } from "../util/composeEventHandlers.js";
import copy from "../util/copy.js";
/**
 * A button component that copies text to the clipboard when clicked.
 *
 * @see [📝 Documentation](https://aksel.nav.no/komponenter/core/copybutton)
 * @see 🏷️ {@link CopyButtonProps}
 *
 * @example
 * ```jsx
      <CopyButton copyText="3.14" />
 * ```
 */
export const CopyButton = forwardRef((_a, ref) => {
    var { className, copyText, text, activeText = "Kopiert!", variant = "neutral", size = "medium", onActiveChange, icon, activeIcon, activeDuration = 2000, title = "Kopier", activeTitle = "Kopiert", iconPosition = "left", onClick } = _a, rest = __rest(_a, ["className", "copyText", "text", "activeText", "variant", "size", "onActiveChange", "icon", "activeIcon", "activeDuration", "title", "activeTitle", "iconPosition", "onClick"]);
    const [active, setActive] = useState(false);
    const timeoutRef = useRef();
    useEffect(() => {
        return () => {
            timeoutRef.current && clearTimeout(timeoutRef.current);
        };
    }, []);
    const handleClick = () => {
        timeoutRef.current && clearTimeout(timeoutRef.current);
        copy(copyText);
        setActive(true);
        onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(true);
        timeoutRef.current = window.setTimeout(() => {
            setActive(false);
            onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(false);
        }, activeDuration);
    };
    const copyIcon = (React.createElement("span", { className: "navds-copybutton__icon" }, active
        ? activeIcon !== null && activeIcon !== void 0 ? activeIcon : (React.createElement(CheckmarkIcon, { "aria-hidden": !!text, title: text ? undefined : activeTitle }))
        : icon !== null && icon !== void 0 ? icon : (React.createElement(FilesIcon, { "aria-hidden": !!text, title: text ? undefined : title }))));
    return (React.createElement("button", Object.assign({ ref: ref, type: "button" }, rest, { "aria-live": "polite", className: cl("navds-copybutton", className, `navds-copybutton--${size}`, `navds-copybutton--${variant}`, {
            "navds-copybutton--icon-only": !text,
            "navds-copybutton--icon-right": iconPosition === "right",
            "navds-copybutton--active": active,
        }), onClick: composeEventHandlers(onClick, handleClick) }),
        React.createElement("span", { className: "navds-copybutton__content" },
            iconPosition === "left" && copyIcon,
            text &&
                (active ? (React.createElement(Label, { as: "span", size: size === "medium" ? "medium" : "small", "aria-live": "polite" }, activeText)) : (React.createElement(Label, { as: "span", size: size === "medium" ? "medium" : "small", "aria-live": "polite" }, text))),
            iconPosition === "right" && copyIcon)));
});
export default CopyButton;
//# sourceMappingURL=CopyButton.js.map